import React from 'react'
import { Dialog } from '@headlessui/react'
import { CheckCircleIcon, XCircleIcon } from '@heroicons/react/24/outline'
import { clsx } from 'clsx'

interface UploadProgressModalProps {
  isOpen: boolean
  videoUploadStatus: 'pending' | 'uploading' | 'success' | 'error'
  reportGenerationStatus: 'pending' | 'generating' | 'success' | 'error'
  videoProgress?: number
  currentAttempt?: number
  maxAttempts?: number
  errorMessage?: string
  onRetry?: () => void
}

const UploadProgressModal: React.FC<UploadProgressModalProps> = ({
  isOpen,
  videoUploadStatus,
  reportGenerationStatus,
  videoProgress = 0,
  currentAttempt = 1,
  maxAttempts = 3,
  errorMessage,
  onRetry
}) => {
  const getStatusIcon = (status: string, isActive: boolean = false) => {
    switch (status) {
      case 'success':
        return <CheckCircleIcon className="w-6 h-6 text-green-500" />
      case 'error':
        return <XCircleIcon className="w-6 h-6 text-red-500" />
      case 'uploading':
      case 'generating':
        return (
          <div className="w-6 h-6 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
        )
      default:
        return (
          <div className={clsx(
            "w-6 h-6 rounded-full border-2",
            isActive ? "border-blue-500 bg-blue-50" : "border-gray-300"
          )} />
        )
    }
  }

  const getStatusText = (status: string) => {
    switch (status) {
      case 'pending':
        return 'Waiting...'
      case 'uploading':
        return currentAttempt > 1 ? `Uploading (attempt ${currentAttempt}/${maxAttempts})...` : 'Uploading...'
      case 'generating':
        return 'Starting...'
      case 'success':
        return 'Completed'
      case 'error':
        return 'Failed'
      default:
        return 'Unknown'
    }
  }

  const isCompleting = videoUploadStatus === 'success' && reportGenerationStatus === 'success'
  const hasErrors = videoUploadStatus === 'error' || reportGenerationStatus === 'error'

  return (
    <Dialog
      open={isOpen}
      onClose={() => {}} // Prevent closing during processing
      className="relative z-50"
    >
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/30" aria-hidden="true" />
      
      {/* Full-screen container */}
      <div className="fixed inset-0 flex items-center justify-center p-4">
        <Dialog.Panel className="mx-auto max-w-md w-full bg-white rounded-lg shadow-lg">
          <div className="p-6">
            {/* Hidden focusable element for FocusTrap */}
            <button className="sr-only" tabIndex={0}>Focus trap</button>
            {/* Header */}
            <div className="text-center mb-6">
              <Dialog.Title className="text-xl font-semibold text-gray-900 mb-2">
                {isCompleting 
                  ? 'Interview Completed Successfully!'
                  : hasErrors
                    ? 'Processing Issues'
                    : 'Processing Interview Data'
                }
              </Dialog.Title>
              <p className="text-sm text-gray-600">
                {isCompleting
                  ? 'Preparing to redirect...'
                  : hasErrors
                    ? 'Some operations failed. Please check the details below.'
                    : 'Please wait while we save your interview data.'
                }
              </p>
            </div>

            {/* Progress Steps */}
            <div className="space-y-4">
              {/* Video Upload Step */}
              <div className="flex items-center space-x-3">
                {getStatusIcon(videoUploadStatus, videoUploadStatus === 'uploading')}
                <div className="flex-1">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-gray-900">
                      Upload Interview Recording
                    </span>
                    <span className="text-xs text-gray-500">
                      {getStatusText(videoUploadStatus)}
                    </span>
                  </div>
                  {videoUploadStatus === 'uploading' && videoProgress > 0 && (
                    <div className="mt-1 w-full bg-gray-200 rounded-full h-1.5">
                      <div 
                        className="bg-blue-500 h-1.5 rounded-full transition-all duration-300"
                        style={{ width: `${videoProgress}%` }}
                      />
                    </div>
                  )}
                  {videoUploadStatus === 'error' && errorMessage && (
                    <div className="mt-1 text-xs text-red-600">
                      {errorMessage}
                    </div>
                  )}
                </div>
              </div>

              {/* Report Generation Step */}
              <div className="flex items-center space-x-3">
                {getStatusIcon(reportGenerationStatus, reportGenerationStatus === 'generating')}
                <div className="flex-1">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-gray-900">
                      Process Interview Report
                    </span>
                    <span className="text-xs text-gray-500">
                      {getStatusText(reportGenerationStatus)}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Completion Message */}
            {isCompleting && (
              <div className="mt-6 p-4 bg-green-50 rounded-lg">
                <div className="flex items-center">
                  <CheckCircleIcon className="w-5 h-5 text-green-500 mr-2" />
                  <span className="text-sm font-medium text-green-800">
                    Interview data saved and report generated successfully!
                  </span>
                </div>
                <p className="text-sm text-green-700 mt-1">
                  Your interview has been processed completely. Redirecting you to the completion page...
                </p>
              </div>
            )}

            {/* Error Message */}
            {hasErrors && (
              <div className="mt-6 p-4 bg-red-50 rounded-lg">
                <div className="flex items-center">
                  <XCircleIcon className="w-5 h-5 text-red-500 mr-2" />
                  <span className="text-sm font-medium text-red-800">
                    Some operations failed
                  </span>
                </div>
                <p className="text-sm text-red-700 mt-1">
                  Your interview data may not be completely saved. Please try again or contact support if this persists.
                </p>
                <button 
                  className="mt-2 px-3 py-1 text-xs bg-red-100 text-red-700 rounded hover:bg-red-200 focus:outline-none focus:ring-2 focus:ring-red-500"
                  onClick={onRetry || (() => window.location.reload())}
                >
                  Try Again
                </button>
              </div>
            )}

            {/* Loading Indicator */}
            {!isCompleting && !hasErrors && (
              <div className="mt-6 text-center">
                <div className="inline-flex items-center text-sm text-gray-600">
                  <div className="w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin mr-2" />
                  Processing your interview data...
                </div>
              </div>
            )}
          </div>
        </Dialog.Panel>
      </div>
    </Dialog>
  )
}

export default UploadProgressModal